/**
 * Workspace Context
 * Provides saved generation state and CRUD operations across the app.
 * Phase 1: localStorage persistence. Phase 2: server sync.
 *
 * Terminology (see ADR-012):
 *   Generation = one "Explain" click result (the saved entry)
 *   Insight    = one hoverable element with S/C/P layers
 *   Lens       = the viewing perspective ("through" concept)
 */
import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react'
import { extractLensConstructs } from '../utils/lensParser'

// --- Data Model ---

export interface WorkspaceInsight {
  conceptName: string
  hasContent: boolean
}

export interface WorkspaceGeneration {
  id: string
  title: string                    // First non-trivial code line (~40 chars)
  contentFingerprint: string       // hash(code + source + target) — identity
  code: string
  htmlContent: string
  sourceLanguage: string
  targetLanguage: string
  insights: WorkspaceInsight[]
  images?: Record<number, string>  // insightIndex → base64 data URL (from NotebookView)
  generationMetadata: {
    model?: string
    provider?: string
    promptVersion?: string
    quality?: 'fast' | 'quality'
  }
  shareId?: string
  createdAt: string
  lastViewedAt: string
}

export interface WorkspaceGroup {
  key: string                       // e.g. "cpp→rust"
  sourceLanguage: string
  targetLanguage: string
  generations: WorkspaceGeneration[]
}

export interface WorkspaceStats {
  totalGenerations: number
  totalInsights: number
  totalShared: number
}

// --- Context Interface ---

interface WorkspaceContextType {
  generations: WorkspaceGeneration[]
  selectedGenerationId: string | null
  selectedInsightIndex: number | null
  searchQuery: string
  expandedGroups: Set<string>
  expandedGenerations: Set<string>

  // Actions
  saveGeneration: (data: Omit<WorkspaceGeneration, 'id' | 'createdAt' | 'lastViewedAt' | 'insights' | 'contentFingerprint' | 'title'> & { title?: string }) => string
  selectGeneration: (id: string | null, insightIndex?: number | null) => void
  deleteGeneration: (id: string) => void
  setSearchQuery: (query: string) => void
  toggleGroup: (key: string) => void
  toggleGeneration: (id: string) => void

  // Derived
  getGroupedGenerations: () => WorkspaceGroup[]
  getStats: () => WorkspaceStats
  getSelectedGeneration: () => WorkspaceGeneration | null
}

const WorkspaceContext = createContext<WorkspaceContextType | undefined>(undefined)

const STORAGE_KEY = 'syntaxlens-workspace-insights'
const MAX_GENERATIONS = 50

// --- Helper: Generate ID ---
function generateId(): string {
  return `ws-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
}

// --- Helper: Content fingerprint (same pattern as share's contentFingerprint) ---
function generateFingerprint(code: string, source: string, target: string): string {
  const input = `${code.trim()}|${source}|${target}`
  let hash = 0
  for (let i = 0; i < input.length; i++) {
    const char = input.charCodeAt(i)
    hash = ((hash << 5) - hash) + char
    hash |= 0
  }
  return `fp-${Math.abs(hash).toString(36)}`
}

// --- Helper: Derive title from code (ADR-012 decision 4) ---
function deriveTitleFromCode(code: string): string {
  const lines = code.split('\n')
  for (const line of lines) {
    const trimmed = line.trim()
    if (!trimmed) continue
    if (trimmed.startsWith('//') || trimmed.startsWith('#') || trimmed.startsWith('/*') || trimmed.startsWith('*')) continue
    if (trimmed.startsWith('import ') || trimmed.startsWith('from ') || trimmed.startsWith('require(')) continue
    if (trimmed.startsWith('#!') || trimmed.startsWith('#pragma') || trimmed.startsWith('#include')) continue
    if (trimmed.startsWith('using ') || trimmed.startsWith('package ')) continue
    return trimmed.length > 40 ? trimmed.slice(0, 40) + '...' : trimmed
  }
  const first = lines[0]?.trim() || 'Untitled'
  return first.length > 40 ? first.slice(0, 40) + '...' : first
}

// --- Helper: Load from localStorage ---
function loadGenerations(): WorkspaceGeneration[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (raw) {
      const parsed = JSON.parse(raw)
      if (Array.isArray(parsed)) {
        // Migrate old format: rename 'constructs' → 'insights' if needed
        return parsed.map((g: any) => ({
          ...g,
          insights: g.insights || g.constructs || [],
        }))
      }
    }
  } catch (e) {
    console.warn('Failed to load workspace generations:', e)
  }
  return []
}

// --- Helper: Save to localStorage ---
function persistGenerations(generations: WorkspaceGeneration[]) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(generations))
  } catch (e) {
    console.warn('Failed to persist workspace generations:', e)
  }
}

// --- Helper: Extract insights from HTML ---
function extractInsightsSummary(html: string): WorkspaceInsight[] {
  try {
    const constructs = extractLensConstructs(html)
    return constructs.map(c => ({
      conceptName: c.conceptName,
      hasContent: !!(c.surface || c.concept || c.paradigm),
    }))
  } catch {
    return []
  }
}

// --- Provider ---

export function WorkspaceProvider({ children }: { children: ReactNode }) {
  const [generations, setGenerations] = useState<WorkspaceGeneration[]>(() => loadGenerations())
  const [selectedGenerationId, setSelectedGenerationId] = useState<string | null>(null)
  const [selectedInsightIndex, setSelectedInsightIndex] = useState<number | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set())
  const [expandedGenerations, setExpandedGenerations] = useState<Set<string>>(new Set())

  // Persist on change
  useEffect(() => {
    persistGenerations(generations)
  }, [generations])

  // Auto-expand first group on load
  useEffect(() => {
    if (generations.length > 0 && expandedGroups.size === 0) {
      const firstKey = `${generations[0].sourceLanguage}→${generations[0].targetLanguage}`
      setExpandedGroups(new Set([firstKey]))
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const saveGeneration = useCallback((data: Omit<WorkspaceGeneration, 'id' | 'createdAt' | 'lastViewedAt' | 'insights' | 'contentFingerprint' | 'title'> & { title?: string }) => {
    const fingerprint = generateFingerprint(data.code, data.sourceLanguage, data.targetLanguage)
    const title = data.title || deriveTitleFromCode(data.code)
    const insights = extractInsightsSummary(data.htmlContent)
    const now = new Date().toISOString()

    // ADR-012 decision 3: duplicate = update, not create
    const existingIndex = generations.findIndex(g => g.contentFingerprint === fingerprint)

    if (existingIndex !== -1) {
      const existing = generations[existingIndex]
      setGenerations(prev => prev.map((gen, idx) =>
        idx === existingIndex
          ? {
              ...gen,
              // Prefer a fresh AI title from this re-generation; keep the old title otherwise.
              // Without this, a first-call LLM failure (code-derived fallback title) would
              // persist forever even after a later call emits a proper session title.
              title: data.title || gen.title,
              htmlContent: data.htmlContent,
              insights,
              images: data.images ? { ...gen.images, ...data.images } : gen.images,
              generationMetadata: data.generationMetadata,
              lastViewedAt: now,
            }
          : gen
      ))
      return existing.id
    }

    const id = generateId()
    const newGeneration: WorkspaceGeneration = {
      ...data,
      id,
      title,
      contentFingerprint: fingerprint,
      insights,
      createdAt: now,
      lastViewedAt: now,
    }

    setGenerations(prev => {
      const updated = [newGeneration, ...prev]
      if (updated.length > MAX_GENERATIONS) {
        return updated.slice(0, MAX_GENERATIONS)
      }
      return updated
    })

    return id
  }, [generations])

  const selectGeneration = useCallback((id: string | null, insightIndex?: number | null) => {
    setSelectedGenerationId(id)
    setSelectedInsightIndex(insightIndex ?? null)
    if (id) {
      setExpandedGenerations(prev => {
        const next = new Set(prev)
        next.add(id)
        return next
      })
      setGenerations(prev => prev.map(gen =>
        gen.id === id ? { ...gen, lastViewedAt: new Date().toISOString() } : gen
      ))
    }
  }, [])

  const deleteGeneration = useCallback((id: string) => {
    setGenerations(prev => prev.filter(gen => gen.id !== id))
    if (selectedGenerationId === id) {
      setSelectedGenerationId(null)
      setSelectedInsightIndex(null)
    }
  }, [selectedGenerationId])

  const toggleGroup = useCallback((key: string) => {
    setExpandedGroups(prev => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }, [])

  const toggleGeneration = useCallback((id: string) => {
    setExpandedGenerations(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }, [])

  const getGroupedGenerations = useCallback((): WorkspaceGroup[] => {
    const query = searchQuery.toLowerCase()
    const filtered = searchQuery
      ? generations.filter(g =>
          g.title.toLowerCase().includes(query) ||
          g.code.toLowerCase().includes(query) ||
          g.insights.some(i => i.conceptName.toLowerCase().includes(query))
        )
      : generations

    const groupMap = new Map<string, WorkspaceGeneration[]>()

    for (const gen of filtered) {
      const key = `${gen.sourceLanguage}→${gen.targetLanguage}`
      if (!groupMap.has(key)) {
        groupMap.set(key, [])
      }
      groupMap.get(key)!.push(gen)
    }

    return Array.from(groupMap.entries()).map(([key, gens]) => ({
      key,
      sourceLanguage: gens[0].sourceLanguage,
      targetLanguage: gens[0].targetLanguage,
      generations: gens,
    }))
  }, [generations, searchQuery])

  const getStats = useCallback((): WorkspaceStats => {
    return {
      totalGenerations: generations.length,
      totalInsights: generations.reduce((sum, g) => sum + g.insights.length, 0),
      totalShared: generations.filter(g => g.shareId).length,
    }
  }, [generations])

  const getSelectedGeneration = useCallback((): WorkspaceGeneration | null => {
    if (!selectedGenerationId) return null
    return generations.find(g => g.id === selectedGenerationId) || null
  }, [generations, selectedGenerationId])

  return (
    <WorkspaceContext.Provider
      value={{
        generations,
        selectedGenerationId,
        selectedInsightIndex,
        searchQuery,
        expandedGroups,
        expandedGenerations,
        saveGeneration,
        selectGeneration,
        deleteGeneration,
        setSearchQuery,
        toggleGroup,
        toggleGeneration,
        getGroupedGenerations,
        getStats,
        getSelectedGeneration,
      }}
    >
      {children}
    </WorkspaceContext.Provider>
  )
}

// --- Hook ---

export function useWorkspace() {
  const context = useContext(WorkspaceContext)
  if (!context) {
    throw new Error('useWorkspace must be used within a WorkspaceProvider')
  }
  return context
}
